import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card, List, Input, Button, Avatar, Badge, Empty, message, Typography, Spin, Tag, Grid, Checkbox, Popconfirm } from "antd";
import { SendOutlined, UserOutlined, ReloadOutlined, DisconnectOutlined, ArrowLeftOutlined, DeleteOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { getConversations, getConversation, getCustomerService, sendMessage, deleteConversations } from "../services/api";
import { PageLoadingOverlay } from "../components/RoomLoadingScene";

const { Text } = Typography;
const conversationCache = new Map();

const MessageScreen = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const currentUserId = JSON.parse(localStorage.getItem("user") || "{}").id;
    const cachedConversations = conversationCache.get(currentUserId);
    const [conversations, setConversations] = useState(cachedConversations || []);
    const [selectedUser, setSelectedUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState("");
    const [loading, setLoading] = useState(false);
    const [conversationsLoading, setConversationsLoading] = useState(!cachedConversations);
    const [sending, setSending] = useState(false);
    const [connected, setConnected] = useState(true);
    const [retryCount, setRetryCount] = useState(0);
    const [selectedConversationIds, setSelectedConversationIds] = useState([]);
    const [deleting, setDeleting] = useState(false);
    const messagesEndRef = useRef(null);
    const customerServiceOpening = useRef(false);
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    // 自动滚动到底部
    useEffect(() => { 
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); 
    }, [messages]);

    // 加载对话列表
    const loadConversations = useCallback(async (isRetry = false) => {
        try {
            if (!isRetry) setConversationsLoading(true);
            if (isRetry) setRetryCount(prev => prev + 1);
            const res = await getConversations();
            setConversations(res);
            conversationCache.set(currentUserId, res);
            setConnected(true);
            setRetryCount(0);
        } catch (e) {
            console.error("加载对话列表失败:", e);
            setConnected(false);
            if (!isRetry) {
                message.error("加载对话列表失败，请检查网络连接");
            }
        } finally {
            if (!isRetry) setConversationsLoading(false);
        }
    }, [currentUserId]);

    // 加载消息
    const loadMessages = useCallback(async (userId, isRetry = false) => {
        try {
            if (!isRetry) setLoading(true);
            const res = await getConversation(userId);
            setMessages(res.data || []);
            setConversations(prev => prev.map(item => item.other_user_id === userId ? { ...item, unread_count: 0 } : item));
            window.dispatchEvent(new Event("message-unread-refresh"));
            setConnected(true);
        } catch (e) {
            console.error("加载消息失败:", e);
            setConnected(false);
            if (!isRetry) {
                message.error("加载消息失败");
            }
        } finally {
            if (!isRetry) setLoading(false);
        }
    }, []);

    // 初始化加载
    useEffect(() => { 
        loadConversations(Boolean(cachedConversations));
        const interval = setInterval(() => loadConversations(true), 10000);
        return () => clearInterval(interval);
    }, [loadConversations]);

    // “联系客服”始终打开管理员客服会话；即使此前没有聊过，也能直接创建新对话。
    useEffect(() => {
        const shouldOpenCustomerService = new URLSearchParams(location.search).get("contact") === "admin";
        if (!shouldOpenCustomerService) {
            customerServiceOpening.current = false;
            return;
        }
        if (customerServiceOpening.current) return;
        customerServiceOpening.current = true;
        let active = true;
        getCustomerService()
            .then(service => {
                if (!active) return;
                const target = {
                    other_user_id: service.id,
                    other_username: service.username || "admin",
                    last_message: "",
                    unread_count: 0,
                    is_customer_service: true
                };
                setSelectedUser(target);
                setConversations(prev => prev.some(item => item.other_user_id === target.other_user_id) ? prev : [target, ...prev]);
            })
            .catch(error => {
                if (active) message.error(error?.error || "暂时无法连接客服，请稍后重试");
            })
            .finally(() => {
                if (active) navigate("/messages", { replace: true });
            });
        return () => { active = false; };
    }, [location.search, navigate]);

    // 当选择用户时加载消息
    useEffect(() => { 
        if (selectedUser) {
            loadMessages(selectedUser.other_user_id);
            const interval = setInterval(() => loadMessages(selectedUser.other_user_id, true), 10000);
            return () => clearInterval(interval);
        }
    }, [selectedUser, loadMessages]);

    // 发送消息
    const handleSend = async () => {
        if (!inputText.trim() || sending) return;
        
        try {
            setSending(true);
            await sendMessage({ 
                receiver_id: selectedUser.other_user_id, 
                content: inputText 
            });
            
            // 本地添加消息
            setMessages(prev => [...prev, { 
                id: Date.now(), 
                sender_id: currentUserId, 
                content: inputText, 
                created_at: new Date().toISOString() 
            }]);
            
            setInputText("");
            loadConversations(true);
            setConnected(true);
        } catch (e) {
            console.error("发送消息失败:", e);
            message.error("发送失败，请重试");
            setConnected(false);
        } finally {
            setSending(false);
        }
    };

    // 重试加载
    const handleRetry = () => {
        if (selectedUser) {
            loadMessages(selectedUser.other_user_id, true);
        } else {
            loadConversations(true);
        }
    };

    const toggleConversation = (userId, checked) => {
        setSelectedConversationIds(prev => checked ? [...new Set([...prev, userId])] : prev.filter(id => id !== userId));
    };

    const handleSelectAll = (checked) => {
        setSelectedConversationIds(checked ? conversations.map(item => item.other_user_id) : []);
    };

    const handleBatchDelete = async () => {
        if (selectedConversationIds.length === 0 || deleting) return;
        try {
            setDeleting(true);
            await deleteConversations(selectedConversationIds);
            if (selectedUser && selectedConversationIds.includes(selectedUser.other_user_id)) {
                setSelectedUser(null);
                setMessages([]);
            }
            setConversations(prev => prev.filter(item => !selectedConversationIds.includes(item.other_user_id)));
            setSelectedConversationIds([]);
            window.dispatchEvent(new Event("message-unread-refresh"));
            message.success("已删除选中的对话");
        } catch (e) {
            message.error("删除失败，请重试");
        } finally {
            setDeleting(false);
        }
    };

    // 渲染连接状态
    const renderConnectionStatus = () => {
        if (connected) return null;
        return (
            <Tag icon={<DisconnectOutlined />} color="error">
                连接断开
                <Button 
                    type="link" 
                    size="small" 
                    icon={<ReloadOutlined />} 
                    onClick={handleRetry}
                    style={{ marginLeft: 4 }}
                >
                    重试
                </Button>
            </Tag>
        );
    };

    return (
        <div className={`message-layout ${selectedUser ? "has-selection" : ""}`}>
            {(conversationsLoading || loading) && <PageLoadingOverlay caption="正在加载消息，请稍候…" />}
            {/* 左侧对话列表 */}
            <Card className="message-list"
                bodyStyle={{ padding: 0 }}
                title="消息列表"
                extra={renderConnectionStatus()}
            >
                {conversations.length === 0 ? (
                    <Empty description="暂无对话" style={{ marginTop: 40 }} />
                ) : (
                    <>
                        <div className="message-batch-toolbar">
                            <Checkbox
                                checked={selectedConversationIds.length === conversations.length}
                                indeterminate={selectedConversationIds.length > 0 && selectedConversationIds.length < conversations.length}
                                onChange={e => handleSelectAll(e.target.checked)}
                            >全选</Checkbox>
                            <Popconfirm
                                title={`删除选中的 ${selectedConversationIds.length} 个对话？`}
                                description="只会从您的消息列表中清除，不影响对方。"
                                okText="确认删除"
                                cancelText="取消"
                                okButtonProps={{ danger: true }}
                                disabled={selectedConversationIds.length === 0}
                                onConfirm={handleBatchDelete}
                            >
                                <Button danger size="small" icon={<DeleteOutlined />} loading={deleting} disabled={selectedConversationIds.length === 0}>
                                    批量删除{selectedConversationIds.length > 0 ? ` (${selectedConversationIds.length})` : ""}
                                </Button>
                            </Popconfirm>
                        </div>
                        <List
                            dataSource={conversations}
                            renderItem={item => (
                            <List.Item 
                                onClick={() => setSelectedUser(item)}
                                className="message-conversation-item"
                                style={{ 
                                    padding: "12px 16px", 
                                    cursor: "pointer", 
                                    background: selectedUser?.other_user_id === item.other_user_id ? "#e6f7ff" : "transparent",
                                    borderLeft: selectedUser?.other_user_id === item.other_user_id ? "3px solid #1890ff" : "3px solid transparent"
                                }}
                            >
                                <Checkbox
                                    checked={selectedConversationIds.includes(item.other_user_id)}
                                    onClick={e => e.stopPropagation()}
                                    onChange={e => toggleConversation(item.other_user_id, e.target.checked)}
                                    style={{ marginRight: 10 }}
                                    aria-label={`选择与 ${item.is_customer_service ? "平台客服" : item.other_username} 的对话`}
                                />
                                <List.Item.Meta
                                    avatar={
                                        <Badge count={item.unread_count}>
                                            <Avatar icon={<UserOutlined />} style={{ backgroundColor: "#1890ff" }} />
                                        </Badge>
                                    }
                                    title={<span style={{ color: "#141414", fontWeight: item.unread_count ? 700 : 500 }}>
                                        {item.is_customer_service ? `平台客服（${item.other_username}）` : item.other_username}
                                        {item.is_customer_service && <Tag color="blue" style={{ marginLeft: 6 }}>官方</Tag>}
                                    </span>}
                                    description={
                                        <Text ellipsis style={{ color: "#262626", fontWeight: item.unread_count ? 600 : 400 }}>
                                            {item.last_message || "暂无消息"}
                                        </Text>
                                    }
                                />
                            </List.Item>
                            )}
                        />
                    </>
                )}
            </Card>

            {/* 右侧消息区域 */}
            <Card className="message-chat"
                bodyStyle={{ height: "100%", display: "flex", flexDirection: "column", padding: 0, minHeight: 0 }}
            >
                {selectedUser ? (
                    <>
                        {/* 消息头部 */}
                        <div className="message-chat-header" style={{ 
                            padding: 16, 
                            borderBottom: "1px solid #f0f0f0", 
                            fontWeight: "bold",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                        }}>
                            <span style={{ display: "flex", alignItems: "center", gap: 8, color: "#141414" }}>
                                {isMobile && <Button type="text" aria-label="返回消息列表" icon={<ArrowLeftOutlined />} onClick={() => setSelectedUser(null)} />}
                                {selectedUser.is_customer_service ? `平台客服（${selectedUser.other_username}）` : selectedUser.other_username}
                            </span>
                            {!connected && (
                                <Button 
                                    type="link" 
                                    icon={<ReloadOutlined />} 
                                    onClick={handleRetry}
                                >
                                    重新连接
                                </Button>
                            )}
                        </div>

                        {/* 消息列表 */}
                        <div className="message-scroll-area">
                            {loading ? (
                                <div style={{ textAlign: "center", padding: 40 }}>
                                    <Spin tip="加载消息中..." />
                                </div>
                            ) : messages.length === 0 ? (
                                <Empty description="暂无消息" />
                            ) : (
                                messages.map(msg => (
                                    <div 
                                        key={msg.id} 
                                        style={{ 
                                            display: "flex", 
                                            justifyContent: msg.sender_id === currentUserId ? "flex-end" : "flex-start", 
                                            marginBottom: 16 
                                        }}
                                    >
                                        <div style={{ 
                                            maxWidth: "78%", 
                                            padding: "10px 14px", 
                                            borderRadius: 12, 
                                            background: msg.sender_id === currentUserId ? "#d9efff" : "#ffffff", 
                                            color: "#141414",
                                            boxShadow: "0 1px 2px rgba(0,0,0,0.1)"
                                        }}>
                                            <div style={{ marginBottom: 4 }}>{msg.content}</div>
                                            <div style={{ 
                                                fontSize: 10, 
                                                color: "#737373",
                                                textAlign: "right"
                                            }}>
                                                {new Date(msg.created_at).toLocaleTimeString()}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* 输入区域 */}
                        <div className="message-input-area" style={{ 
                            padding: 16, 
                            borderTop: "1px solid #f0f0f0", 
                            display: "flex", 
                            gap: 8,
                            background: "#fff"
                        }}>
                            <Input 
                                value={inputText} 
                                onChange={(e) => setInputText(e.target.value)} 
                                onPressEnter={handleSend}
                                placeholder={connected ? "输入消息..." : "连接断开，请重试"}
                                disabled={!connected || sending}
                                style={{ flex: 1 }}
                            />
                            <Button 
                                type="primary" 
                                icon={<SendOutlined />} 
                                onClick={handleSend}
                                loading={sending}
                                disabled={!connected || !inputText.trim()}
                            >
                                发送
                            </Button>
                        </div>
                    </>
                ) : (
                    <div style={{ 
                        flex: 1, 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: 16
                    }}>
                        <Empty description="选择一个对话开始聊天" />
                        {!connected && (
                            <Button type="primary" icon={<ReloadOutlined />} onClick={handleRetry}>
                                重新连接
                            </Button>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
};

export default MessageScreen;
